import { type RefObject, useEffect, useCallback } from 'react';

interface InfiniteScrollProps {
  targetRef: RefObject<HTMLDivElement>;
  rootRef: RefObject<HTMLDivElement>;
  hasMore: boolean;
  loadMore: () => void;
}

const useInfiniteScroll = ({
  targetRef,
  rootRef,
  hasMore,
  loadMore,
}: InfiniteScrollProps) => {
  const handleInfiniteScroll = useCallback(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      const target = entries[0];

      if (target.isIntersecting) {
        if (hasMore) {
          loadMore();
        } else {
          observer.unobserve(target.target);
        }
      }
    },
    [loadMore, hasMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleInfiniteScroll, {
      root: rootRef.current,
      rootMargin: '20px',
      threshold: 0,
    });

    const target = targetRef.current;

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [handleInfiniteScroll, rootRef, targetRef]);
};

export default useInfiniteScroll;
